import { FileText, X } from "lucide-react";
import { Button } from "../ui/button";

/**
 * BookingDraftBanner — Phase 1, P1
 * ────────────────────────────────
 * Inline banner shown at the top of the New Order dialog when a saved
 * draft is detected for the current user. Two actions: Resume (restore
 * the form state) and Discard (delete the draft).
 *
 * Renders nothing if no draft is present.
 */
function formatSavedAt(iso) {
  if (!iso) return "earlier";
  const saved = new Date(iso).getTime();
  if (Number.isNaN(saved)) return "earlier";
  const ageMs = Date.now() - saved;
  if (ageMs < 60_000) return "just now";
  const mins = Math.floor(ageMs / 60_000);
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export default function BookingDraftBanner({ draft, onResume, onDiscard }) {
  if (!draft) return null;
  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5 mb-3 sm:mb-4"
      data-testid="booking-draft-banner"
    >
      <FileText className="h-4 w-4 shrink-0 text-[#FF6B00]" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 leading-tight">
          Resume your draft
        </p>
        <p className="text-xs text-slate-600 truncate">
          Saved {formatSavedAt(draft.saved_at)} — pick up where you left off.
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        onClick={onResume}
        className="h-8 px-3 rounded-lg bg-[#FF6B00] hover:bg-[#e55f00] text-white text-xs shrink-0"
        data-testid="booking-draft-resume-btn"
      >
        Restore
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onDiscard}
        className="h-8 w-8 p-0 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-orange-100 shrink-0"
        aria-label="Discard draft"
        data-testid="booking-draft-discard-btn"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
